
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from 'vue'
import type { MetaInfo } from 'vue-meta'
import { createAmpOfferInstance, MobileOffer, HomeOffer, BundleOffer, OfferID } from '@alao-frontend/core'
import { AnalyticsOffersList } from '@alao-frontend/analytics.plugin'
import DealsSlider from '~/components/DealsSlider/DealsSlider.vue'
import MoreExclusive from '~/components/sections/more-exclusive/MoreExclusive.vue'
import ProvidesDeals from '~/components/sections/providers-deals/ProvidersDeals.vue'
import HowItWorks from '~/components/main-page/HowItWorks.vue'
import ClientsFeedback from '~/components/sections/clients-feedback/ClientsFeedback.vue'
import FaqList from '~/components/sections/faq/FaqList.vue'
import FaqItem from '~/components/sections/faq/FaqItem.vue'
import { FAQItem, useSEOMetaComposer } from '~/composables/seo.composable'
import { FeatureFlags } from '~/core/config/feature-flags'
import AlTab from '~/components/ui/AlTab/AlTab.vue'
import BlackAprilBanner from '~/components/black-april/BlackAprilBanner.vue'
import NewslettersSignupSection from '~/components/sections/NewslettersSignupSection.vue'
import OfferCard from '~/components/offers/OfferCard/OfferCard.vue'
import { $api } from '~/plugins/axios'
import { CURRENCY_NAME } from '~/core/config/constants'

export default defineComponent({
  name: 'BlackApril',
  components: { OfferCard, NewslettersSignupSection, AlTab, HowItWorks, ClientsFeedback, FaqList, FaqItem, MoreExclusive, ProvidesDeals, DealsSlider, BlackAprilBanner },
  setup () {
    const app = getCurrentInstance()!.proxy!
    const listPosition = 'Black April Offer'
    const viewedOffersInViewport = ref<OfferID[]>([])

    const faqList = computed(() => {
      return [
        { question: app.$t('home.faq.accordion.title1'), answer: app.$t('home.faq.accordion.content1') },
        { question: app.$t('home.faq.accordion.title2'), answer: app.$t('home.faq.accordion.content2') },
        { question: app.$t('home.faq.accordion.title3'), answer: app.$t('home.faq.accordion.content3') },
        app.$featureFlags.isEnabled(FeatureFlags.NEW_VOUCHER_PROCESS) && { question: app.$t('home.faq.accordion.title4'), answer: app.$t('home.faq.accordion.content4') },
        { question: app.$t('home.faq.accordion.title5'), answer: app.$t('home.faq.accordion.content5') },
        { question: app.$t('home.faq.accordion.title6'), answer: app.$t('home.faq.accordion.content6') },
        { question: app.$t('home.faq.accordion.title7'), answer: app.$t('home.faq.accordion.content7') },
      ].filter(Boolean) as FAQItem[]
    })

    const activeTab = ref<'mobile' | 'home'>('mobile')
    const mobileSlider = ref<any>(null)
    const homeSlider = ref<any>(null)
    const setActiveTab = (tab: 'mobile' | 'home') => {
      activeTab.value = tab
      if (homeSlider.value && homeSlider.value.$refs.carousel) {
        homeSlider?.value.$refs.carousel.goTo(0)
      }
      if (mobileSlider.value && mobileSlider.value.$refs.carousel) {
        mobileSlider?.value.$refs.carousel.goTo(0)
      }
    }
    const mobileOffers = ref([])
    const homeOffers = ref([])
    const topOffer = ref()

    const onOfferClick = (
      index: number,
      offer: MobileOffer | HomeOffer | BundleOffer,
    ) => {
      app.$analytics.utils.setOffersListPosition(listPosition as AnalyticsOffersList)
      app.$analytics.utils.setOffersIndexPosition(index + 1)

      app.$analytics.GTM.sendEcommerceSelectItemEvent({
        offer,
        currency: CURRENCY_NAME,
        userData: app.$analytics.user.data,
        position: {
          index: app.$analytics.utils.getOffersIndexPosition(),
          name: app.$analytics.utils.getOffersListPosition(),
        },
      })
    }

    const onOfferVisibilityStateChanged = (
      isVisible: boolean,
      index: number,
      offer: MobileOffer | HomeOffer | BundleOffer,
    ) => {
      if (!isVisible || viewedOffersInViewport.value.includes(offer.id)) {
        return
      }

      const offerPosition = index + 1

      viewedOffersInViewport.value.push(offer.id)

      app.$analytics.GTM.sendEcommerceViewItemListEvent({
        offer,
        currency: CURRENCY_NAME,
        userData: app.$analytics.user.data,
        position: {
          index: offerPosition,
          name: listPosition as AnalyticsOffersList,
        },
      })
    }

    onMounted(async () => {
      mobileOffers.value = (await $api.PublicOfferService.searchOffers({
        product_type: 'mobile',
        limit: 5,
      })).items.map(item => createAmpOfferInstance(item))
      homeOffers.value = (await $api.PublicOfferService.searchOffers({
        product_type: 'home',
        limit: 5,
      })).items.map(item => createAmpOfferInstance(item))
      topOffer.value = mobileOffers.value.shift()

      app.$analytics.GTM.sendPageViewEvent({
        routeName: app.$route.name as string,
        pageTitle: app.$metaInfo.title || '',
        fullPath: app.$route.fullPath,
        fromPath: app.$nuxt.context.from ? app.$nuxt.context.from.fullPath : '',
        contentGroup: 'Black April',
      })
    })
    return {
      topOffer,
      mobileOffers,
      homeOffers,
      faqList,
      activeTab,
      setActiveTab,
      onOfferClick,
      onOfferVisibilityStateChanged,
      mobileSlider,
      homeSlider,
    }
  },
  head (): MetaInfo {
    return useSEOMetaComposer(
      this,
      {
        title: this.$t('head.meta.blackApril.title') as string,
        description: this.$t('head.meta.blackApril.description') as string,
        route: { name: 'black-april' },
        faq: {
          items: this.faqList,
        },
      },
    )
  },
})
